import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

import { MatSnackBar } from '@angular/material/snack-bar'

import { LocalMonth, NumberMonth } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { OmaveroYhteisomyyntiDraft, OmaveroYhteisomyyntilaskuLahetetty, OmaveroYhteisomyyntilaskuLahetettyRivi } from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/omavero'
import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { KirjautunutTilintarkastajaService } from 'app/_angular/service/kirjautunut-tilintarkastaja.service'
import { KirjanpitoKuukausiruutu } from 'app/_jaettu-lemonator/model/asiakas'

export interface ListausYhteisomyyntiIlmoitus extends OmaveroYhteisomyyntilaskuLahetetty {
  url: string
  uri: string
  tallennustiedot: string
}

@Component({
  selector: '[app-kirjanpito-tilintarkastus-yhteisomyynti-ilmoitus]',
  templateUrl: './tilintarkastus-yhteisomyynti-ilmoitus.component.html',
  styleUrls: ['./tilintarkastus-yhteisomyynti-ilmoitus.component.css', './tilintarkastus-alv-ilmoitus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitoTilintarkastusYhteisomyyntiIlmoitusComponent implements OnInit, OnDestroy {
  @Input() valittuKuukausiObservable: Observable<LocalMonth>
  @Output() nykyisellaKuukaudellaOnMuutoksia = new EventEmitter<'yes' | 'no' | 'not-saved-yet'>()

  private _ngUnsubscribe = new Subject<void>()

  draftLoadingSubject = new BehaviorSubject<boolean>(true)
  historyLoadingSubject = new BehaviorSubject<boolean>(true)
  private _tallentamattomiaMuutoksiaOverrideSubject = new BehaviorSubject<boolean | null>(null)

  draftObservable: Observable<OmaveroYhteisomyyntiDraft | null>
  aikaisemmatIlmoituksetObservable: Observable<ListausYhteisomyyntiIlmoitus[]>
  tallentamattomiaMuutoksiaObservable: Observable<boolean>
  voidaanLahettaaObservable: Observable<boolean>
  voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable: Observable<boolean>
  kuukausiNumberObservable: Observable<NumberMonth>
  asiakasObservable: Observable<AsiakkaanAvainTiedot>
  errorMessageObservable: Observable<string | null>

  constructor(
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _asiakasUriService: AsiakasUriService,
    private _dateService: DateService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjautunutTilintarkastajaService: KirjautunutTilintarkastajaService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.kuukausiNumberObservable = this.valittuKuukausiObservable.pipe(map(kuukausi => this._dateService.localMonthToNumber(kuukausi)))

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(takeUntil(this._ngUnsubscribe))

    const hyvaksymattomatKirjauksetRaakadataObservable: Observable<KirjanpitoKuukausiruutu[]> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const collectionUri = this._asiakasUriService.annaListausRuutuCollectionUri(asiakas.avain)
          return this._firebase.firestoreCollection<KirjanpitoKuukausiruutu>(collectionUri).where('u', '>', 0).listen()
        }
        return of<KirjanpitoKuukausiruutu[]>([])
      }),
    )

    const hyvaksymattomatKirjauksetKuukaudellaObservable: Observable<KirjanpitoKuukausiruutu[]> = combineLatest([this.kuukausiNumberObservable, hyvaksymattomatKirjauksetRaakadataObservable]).pipe(
      map(([kuukausiNumber, ruudut]) => {
        if (kuukausiNumber && ruudut) {
          return ruudut.filter(a => a.k === kuukausiNumber)
        }
        return []
      }),
    )

    const asiakasJaKuukausiObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.kuukausiNumberObservable])

    this.draftObservable = asiakasJaKuukausiObservable.pipe(
      tap(() => this.draftLoadingSubject.next(true)),
      switchMap(([asiakas, kuukausiNumber]) => {
        if (!asiakas) {
          return of(null)
        }
        const draftUri = this._kirjanpitoUriService.annaYhteisomyyntiIlmoituksenDraftUri(asiakas.avain, kuukausiNumber)
        return this._firebase.firestoreDoc<OmaveroYhteisomyyntiDraft>(draftUri).listen()
      }),
      tap(() => this.draftLoadingSubject.next(false)),
      takeUntil(this._ngUnsubscribe),
    )

    // Lataa kaikki lähetetyt ilmoitukset kyseiselle kuukaudelle
    const aikaisemmatIlmoituksetRaakadataObservable = asiakasJaKuukausiObservable.pipe(
      tap(() => this.historyLoadingSubject.next(true)),
      switchMap(([asiakas, kuukausiNumber]) => {
        if (!asiakas) {
          return of<OmaveroYhteisomyyntilaskuLahetetty[]>([])
        }
        const lahetettyCollectionUri = this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyCollectionUri(asiakas.avain)
        return this._firebase.firestoreCollection<OmaveroYhteisomyyntilaskuLahetetty>(lahetettyCollectionUri).where('jakso', '==', kuukausiNumber).listen()
      }),
      map(ilmoitukset => {
        return ilmoitukset.sort((a, b) => b.lahetetty.toMillis() - a.lahetetty.toMillis())
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.aikaisemmatIlmoituksetObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(startWith(null)),
      this._kirjautunutTilintarkastajaService.tilintarkastajaObservable.pipe(startWith(null)),
      this._asiakasService.nykyinenAsiakasAvainObservable,
      aikaisemmatIlmoituksetRaakadataObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
    ]).pipe(
      map(([kirjanpitaja, tilintarkastaja, asiakas, ilmoitukset, kirjanpitajienNimitiedotMap]) => {
        if ((!kirjanpitaja && !tilintarkastaja) || !asiakas || !ilmoitukset) {
          return []
        }
        const populateDebugUrls = kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER
        return ilmoitukset.map(ilmoitus => {
          const asListaus: ListausYhteisomyyntiIlmoitus = ilmoitus as ListausYhteisomyyntiIlmoitus

          const nimitiedot = kirjanpitajienNimitiedotMap.get(ilmoitus.tallentajaAvain)
          const date = this._dateService.timestampToDate(ilmoitus.lahetetty)
          asListaus.tallennustiedot = (nimitiedot ? nimitiedot.etunimi + ' ' + nimitiedot.sukunimi : 'Lemonator System') + ' ' + this._dateService.muotoilePaivaJaAikaDate(date, 'fi')
          if (populateDebugUrls) {
            asListaus.uri = this._kirjanpitoUriService.annaOmaveroYhteisomyyntiIlmoitusLahetettyUri(asiakas.avain, ilmoitus.lahetysAvain)
            asListaus.url = this._debugService.createFirestoreLink(asListaus.uri)
          } else {
            asListaus.uri = null
            asListaus.url = null
          }
          return asListaus
        })
      }),
      tap(() => this.historyLoadingSubject.next(false)),
      takeUntil(this._ngUnsubscribe),
    )

    const laskettuMuutoksiaObservable = this.draftObservable.pipe(
      map(draft => draft?.tarvitseeLahetyksen ?? false),
      takeUntil(this._ngUnsubscribe),
    )

    // Yhdistä laskettu arvo ja manuaalinen yliajo (käytetään lähetyksen jälkeen)
    this.tallentamattomiaMuutoksiaObservable = combineLatest([laskettuMuutoksiaObservable, this._tallentamattomiaMuutoksiaOverrideSubject]).pipe(
      map(([laskettu, yliajo]) => {
        // Jos yliajo on asetettu (ei null), käytä sitä; muuten käytä laskettua arvoa
        return yliajo !== null ? yliajo : laskettu
      }),
      takeUntil(this._ngUnsubscribe),
    )

    this.errorMessageObservable = combineLatest([this.kuukausiNumberObservable, hyvaksymattomatKirjauksetKuukaudellaObservable]).pipe(
      map(([kuukausiNumber, hyvaksymattomatRuudut]) => {
        if (hyvaksymattomatRuudut?.length && kuukausiNumber) {
          const kuukausi = this._dateService.numberToLocalMonth(kuukausiNumber)
          const tunnistetiedot = kuukausi.month + '/' + kuukausi.year
          return 'Et voi lähettää yhteisömyynti-ilmoitusta ennen kuin olet hyväksynyt kaikki kuukauden ' + tunnistetiedot + ' kirjaukset.'
        }
        return null
      }),
    )

    this.voidaanLahettaaObservable = of(false)

    this.voidaanLahettaaVaikkaTiedotEivatMuuttuneetObservable = of(false)

    // Nollaa yliajo kun kuukausi vaihtuu
    this.kuukausiNumberObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this._tallentamattomiaMuutoksiaOverrideSubject.next(null)
    })

    combineLatest([this.draftObservable, this.tallentamattomiaMuutoksiaObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([draft, onMuutoksia]) => {
        // Jos ei ole rivejä, ei tarvetta yhteisömyynti-ilmoitukselle
        if (!draft || draft.rivit.length === 0) {
          this.nykyisellaKuukaudellaOnMuutoksia.emit('no')
        } else if (onMuutoksia) {
          // Jos on rivejä ja muutoksia, emit 'yes'
          this.nykyisellaKuukaudellaOnMuutoksia.emit('yes')
        } else {
          // Jos on rivejä mutta ei muutoksia, emit 'no'
          this.nykyisellaKuukaudellaOnMuutoksia.emit('no')
        }
      })
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  computeTotalsForAlvRivi(rivit: OmaveroYhteisomyyntilaskuLahetettyRivi[]) {
    const rivitMap = new Map<string, OmaveroYhteisomyyntilaskuLahetettyRivi>()

    for (const rivi of rivit) {
      const nykyinenRivi = rivitMap.get(rivi.alvTunnus) || {
        maaKoodi: rivi.maaKoodi,
        alvTunnus: rivi.alvTunnus,
        tavaramyynnit: 0,
        palvelumyynnit: 0,
        kolmikantamyynnit: 0,
      }
      nykyinenRivi.tavaramyynnit += rivi.tavaramyynnit
      nykyinenRivi.palvelumyynnit += rivi.palvelumyynnit
      nykyinenRivi.kolmikantamyynnit += rivi.kolmikantamyynnit
      rivitMap.set(rivi.alvTunnus, nykyinenRivi)
    }
    return Array.from(rivitMap.values())
  }

  formatNumberMonthForDisplay(numberMonth: NumberMonth): string {
    const month = numberMonth % 100
    const year = Math.floor(numberMonth / 100)
    return `${month} / ${year}`
  }

  // Tilintarkastus-modessa lähetys ei ole mahdollista – tätä metodia ei kutsuta koskaan
  laheta(yliajo: boolean = false) {}
}
